import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const StudentDefaultsSection = ({ teacher, onUpdate }) => {
  const [passwordFormat, setPasswordFormat] = useState(teacher?.default_password_format || 'random');
  const [defaultStatus, setDefaultStatus] = useState(teacher?.default_student_status || 'active');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleSave = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const { error } = await supabase
        ?.from('teachers')
        ?.update({
          default_password_format: passwordFormat,
          default_student_status: defaultStatus,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', teacher?.id);
      if (error) throw error;
      setMsg({ type: 'success', text: 'Student defaults saved.' });
      onUpdate?.({ default_password_format: passwordFormat, default_student_status: defaultStatus });
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Failed to save defaults.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
          <Icon name="Users" size={20} className="text-success" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Student Account Defaults</h2>
          <p className="text-sm text-muted-foreground">Configure default settings applied when creating new student accounts</p>
        </div>
      </div>

      <div className="space-y-5 mb-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Default Password Format</label>
          <select
            value={passwordFormat}
            onChange={e => setPasswordFormat(e?.target?.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          >
            <option value="random">Random (e.g. xk9p2m)</option>
            <option value="name-based">Name-based (e.g. john123)</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">This format is used when auto-generating passwords for new students.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Default Student Status</label>
          <select
            value={defaultStatus}
            onChange={e => setDefaultStatus(e?.target?.value)}
            className="w-full px-3 py-2.5 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          >
            <option value="active">Active — students can log in immediately</option>
            <option value="inactive">Inactive — students cannot log in until activated</option>
          </select>
          <p className="text-xs text-muted-foreground mt-1">New students will be created with this status by default.</p>
        </div>
      </div>

      {msg && (
        <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg mb-4 ${
          msg?.type === 'success' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
        }`}>
          <Icon name={msg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
          {msg?.text}
        </div>
      )}

      <button
        onClick={handleSave}
        disabled={saving}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors"
      >
        {saving ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="Save" size={16} />}
        {saving ? 'Saving...' : 'Save Defaults'}
      </button>
    </div>
  );
};

export default StudentDefaultsSection;
